/**
 * Questionnaire Responses Modal
 *
 * Displays the patient's HCV risk assessment questionnaire responses
 * in a structured, section-grouped format with risk score and counselor notes.
 */

import { AlertCircleIcon, CheckCircle2Icon } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'

interface QuestionnaireResponse {
  question_id: number
  section_name: string
  question_text: string
  selected_response_text: string
  point_value: number
  display_order: number
}

interface QuestionnaireResponsesModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  riskLevel: string
  riskColor: string
  completedAt: string
  counselorSummary?: string | null
  questionnaireBySection: Record<string, QuestionnaireResponse[]>
  totalScore?: number
}

export function QuestionnaireResponsesModal({
  open,
  onOpenChange,
  riskLevel,
  riskColor,
  completedAt,
  counselorSummary,
  questionnaireBySection,
}: QuestionnaireResponsesModalProps) {
  const getRiskBadgeVariant = (level: string): 'default' | 'secondary' | 'destructive' => {
    switch (level.toLowerCase()) {
      case 'high':
      case 'critical':
        return 'destructive'
      case 'moderate':
        return 'secondary'
      default:
        return 'default'
    }
  }

  const getScoreBadgeColorClasses = (points: number): string => {
    // 0 = green (best/no risk), progressively worse from 1-5 (red = worst)
    // Using pastel shades for softer appearance
    if (points === 0) {
      return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50'
    } else if (points === 1) {
      return 'bg-lime-100 dark:bg-lime-900/30 text-lime-800 dark:text-lime-300 hover:bg-lime-200 dark:hover:bg-lime-900/50'
    } else if (points === 2) {
      return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 hover:bg-yellow-200 dark:hover:bg-yellow-900/50'
    } else if (points === 3) {
      return 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 hover:bg-orange-200 dark:hover:bg-orange-900/50'
    } else if (points === 4) {
      return 'bg-orange-200 dark:bg-orange-900/50 text-orange-900 dark:text-orange-200 hover:bg-orange-300 dark:hover:bg-orange-900/70'
    } else {
      // 5 or higher = red (worst/highest risk)
      return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const sections = Object.keys(questionnaireBySection).sort()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh]">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <AlertCircleIcon className="h-5 w-5" />
              Risk Assessment Questionnaire
            </DialogTitle>
            <Badge variant={getRiskBadgeVariant(riskLevel)}>
              {riskLevel} Risk
            </Badge>
          </div>
          <DialogDescription>
            Completed on {formatDate(completedAt)}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[calc(85vh-180px)] overflow-y-auto pr-4">
          <div className="space-y-6">
            {/* Counselor Summary */}
            {counselorSummary && (
              <div className="rounded-lg border bg-muted/50 p-4">
                <div className="text-sm font-medium text-muted-foreground mb-2">
                  Counselor Summary
                </div>
                <div className="text-sm leading-relaxed">{counselorSummary}</div>
              </div>
            )}

            {/* Questionnaire Responses by Section */}
            {sections.length > 0 ? (
              sections.map((sectionName, index) => {
                const responses = questionnaireBySection[sectionName]
                return (
                  <div key={sectionName}>
                    {index > 0 && <Separator className="my-6" />}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold flex items-center gap-2">
                        <CheckCircle2Icon className="h-5 w-5 text-primary" />
                        {sectionName}
                      </h3>
                      <div className="space-y-3">
                        {responses.map((response) => (
                          <div
                            key={response.question_id}
                            className="rounded-lg border bg-card p-4 space-y-2"
                          >
                            <div className="text-sm font-medium text-foreground">
                              {response.question_text}
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="text-sm text-muted-foreground">
                                <span className="font-medium text-foreground">
                                  {response.selected_response_text}
                                </span>
                              </div>
                              <Badge variant="outline" className={`text-xs ${getScoreBadgeColorClasses(response.point_value)}`}>
                                {response.point_value} {response.point_value === 1 ? 'point' : 'points'}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )
              })
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No questionnaire responses available
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
